/**
 * 通用配置管理器
 * 负责通用配置数据的获取、存储、缓存和按需加载
 */

import commonConfigService, { CommonConfig } from '../services/commonConfigService';

// 重新导出 CommonConfig 类型，方便其他模块使用
export type { CommonConfig };

// 配置缓存接口
export interface CommonConfigCache {
  data: CommonConfig[];
  timestamp: number;
  version: string;
}

// 存储配置
const STORAGE_CONFIG = {
  PREFIX: 'common_config_cache_',
  VERSION: '1.0.0',
  EXPIRY_TIME: 30 * 24 * 60 * 60 * 1000, // 30天过期（通用配置变化不频繁，延长缓存时间）
};

class CommonConfigManager {
  private static instance: CommonConfigManager;
  private memoryCache: Map<string, CommonConfigCache> = new Map();
  private loadingPromises: Map<string, Promise<any>> = new Map();
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): CommonConfigManager {
    if (!CommonConfigManager.instance) {
      CommonConfigManager.instance = new CommonConfigManager();
    }
    return CommonConfigManager.instance;
  }

  /**
   * 初始化管理器
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 从localStorage加载所有缓存的配置数据
      await this.loadFromStorage();
      this.isInitialized = true;
    } catch (error) {
      console.error('通用配置管理器初始化失败:', error);
      throw error;
    }
  }

  /**
   * 从localStorage加载所有缓存的配置数据
   */
  private async loadFromStorage(): Promise<void> {
    try {
      const configTypes = ['contact_info', 'website_info', 'record_info'];
      
      for (const configType of configTypes) {
        const cacheKey = this.getStorageKey(configType);
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const cache: CommonConfigCache = JSON.parse(cachedData);
            
            // 无论缓存是否过期，都加载到内存
            // 通用配置变化不频繁，即使过期也优先使用缓存，避免频繁的API调用
            this.memoryCache.set(configType, cache);
          } catch (error) {
            localStorage.removeItem(cacheKey);
          }
        }
      }
      
    } catch (error) {
      console.error('从storage加载配置缓存失败:', error);
    }
  }

  /**
   * 批量预加载所有配置数据（访问网站时调用）
   */
  public async preloadAllConfigs(): Promise<void> {
    try {
      
      // 检查是否已经有批量加载正在进行
      const batchKey = 'batch_preload_all';
      const existingBatchPromise = this.loadingPromises.get(batchKey);
      if (existingBatchPromise) {
        await existingBatchPromise;
        return;
      }

      const batchPromise = this.doBatchPreload();
      this.loadingPromises.set(batchKey, batchPromise);

      try {
        await batchPromise;
      } finally {
        this.loadingPromises.delete(batchKey);
      }
    } catch (error) {
      console.error('批量预加载所有通用配置数据失败:', error);
      throw error;
    }
  }

  /**
   * 执行批量预加载（优化：只在缓存不存在时才调用API）
   */
  private async doBatchPreload(): Promise<void> {
    try {
      const configTypes = ['contact_info', 'website_info', 'record_info'];
      
      // 检查哪些类型需要从API加载（缓存不存在的）
      const typesToLoad: string[] = [];
      for (const type of configTypes) {
        // 检查内存缓存
        const memoryCache = this.memoryCache.get(type);
        if (memoryCache) {
          continue; // 已有缓存，跳过
        }
        
        // 检查storage缓存
        const storageCache = this.getFromStorage(type);
        if (storageCache) {
          // 有storage缓存，加载到内存
          this.memoryCache.set(type, storageCache);
          continue; // 已有缓存，跳过
        }
        
        // 缓存不存在，需要从API加载
        typesToLoad.push(type);
      }

      // 如果没有需要加载的类型，直接返回（避免不必要的API调用）
      if (typesToLoad.length === 0) {
        return;
      }

      // 只为缓存不存在的类型调用API（一次请求获取所有配置）
      const response = await commonConfigService.getList({
        current: 1,
        size: 1000,
        status: '1', // 只获取正常状态的配置
        // 不传 configType，获取所有类型的配置
      });

      if (response.code === 200 && response.data) {
        const records = response.data.records || response.data;
        const allData = Array.isArray(records) ? records : [];
        
        // 按配置类型分组
        const groupedData: { [key: string]: CommonConfig[] } = {};
        
        // 初始化分组
        configTypes.forEach(type => {
          groupedData[type] = [];
        });
        
        // 按类型分组数据
        allData.forEach((config: CommonConfig) => {
          if (config.configType && groupedData[config.configType]) {
            groupedData[config.configType].push(config);
          }
        });
        
        // 分别缓存每个类型的数据
        for (const [configType, data] of Object.entries(groupedData)) {
          await this.setConfigData(configType, data);
        }
        
      } else {
        throw new Error(response.message || '批量获取配置数据失败');
      }
    } catch (error) {
      console.error('批量预加载失败:', error);
      throw error;
    }
  }

  /**
   * 获取指定类型的配置数据（优先从缓存读取，只在缓存完全不存在时才从API获取）
   * 通用配置应该在网站端刷新时加载一次，这里主要从缓存读取
   * 即使缓存过期也优先使用缓存，避免频繁的API调用
   */
  public async getConfigData(configType: string): Promise<CommonConfig[]> {
    // 1. 检查内存缓存（即使过期也使用，避免频繁API调用）
    const memoryCache = this.memoryCache.get(configType);
    if (memoryCache) {
      return memoryCache.data;
    }

    // 2. 检查storage缓存（即使过期也使用，避免频繁API调用）
    const storageCache = this.getFromStorage(configType);
    if (storageCache) {
      // 更新内存缓存
      this.memoryCache.set(configType, storageCache);
      return storageCache.data;
    }

    // 3. 检查批量预加载是否正在进行中
    const batchKey = 'batch_preload';
    const existingBatchPromise = this.loadingPromises.get(batchKey);
    if (existingBatchPromise) {
      await existingBatchPromise;
      // 预加载完成后，再次检查缓存
      const memoryCacheAfterPreload = this.memoryCache.get(configType);
      if (memoryCacheAfterPreload && !this.isExpired(memoryCacheAfterPreload.timestamp)) {
        return memoryCacheAfterPreload.data;
      }
    }

    // 4. 检查是否正在加载中
    const existingPromise = this.loadingPromises.get(configType);
    if (existingPromise) {
      return await existingPromise;
    }

    // 5. 从API获取（仅在缓存不存在且没有正在进行的预加载时）
    const promise = this.fetchFromAPI(configType);
    this.loadingPromises.set(configType, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      this.loadingPromises.delete(configType);
    }
  }

  /**
   * 根据配置键获取配置值（同步方法，从缓存获取）
   * 如果缓存不存在，返回null，调用者可以使用异步方法获取
   */
  public getCachedConfigValue(configKey: string, configType: string): string | null {
    try {
      // 从内存缓存获取
      const memoryCache = this.memoryCache.get(configType);
      if (memoryCache && !this.isExpired(memoryCache.timestamp)) {
        const config = memoryCache.data.find(c => c.configKey === configKey && c.status === '1');
        return config?.configValue || null;
      }
      
      // 从storage缓存获取
      const storageCache = this.getFromStorage(configType);
      if (storageCache && !this.isExpired(storageCache.timestamp)) {
        const config = storageCache.data.find(c => c.configKey === configKey && c.status === '1');
        return config?.configValue || null;
      }
      
      return null;
    } catch (error) {
      console.error('从缓存获取配置值失败:', error);
      return null;
    }
  }

  /**
   * 根据配置键获取配置值
   */
  public async getConfigValue(configKey: string, configType?: string): Promise<string | null> {
    let configs: CommonConfig[] = [];
    
    if (configType) {
      // 如果指定了类型，只在该类型中查找
      configs = await this.getConfigData(configType);
    } else {
      // 如果没有指定类型，在所有类型中查找
      const allTypes = ['contact_info', 'website_info', 'record_info'];
      const allConfigs = await Promise.all(
        allTypes.map(type => this.getConfigData(type))
      );
      configs = allConfigs.flat();
    }
    
    const config = configs.find(c => c.configKey === configKey && c.status === '1');
    return config?.configValue || null;
  }

  /**
   * 获取所有配置数据（按类型分组）
   */
  public async getAllConfigs(): Promise<{ [key: string]: CommonConfig[] }> {
    const configTypes = ['contact_info', 'website_info', 'record_info'];
    const allConfigs: { [key: string]: CommonConfig[] } = {};
    
    for (const configType of configTypes) {
      allConfigs[configType] = await this.getConfigData(configType);
    }
    
    return allConfigs;
  }

  /**
   * 从API获取配置数据
   */
  private async fetchFromAPI(configType: string): Promise<CommonConfig[]> {
    try {
      const response = await commonConfigService.getList({
        current: 1,
        size: 1000,
        configType,
        status: '1', // 只获取正常状态的配置
      });

      if (response.code === 200 && response.data) {
        const records = response.data.records || response.data;
        const data = Array.isArray(records) ? records : [];
        
        // 缓存到内存和storage
        await this.setConfigData(configType, data);
        
        return data;
      } else {
        throw new Error(response.message || '获取配置数据失败');
      }
    } catch (error) {
      console.error(`从API获取配置数据失败: ${configType}`, error);
      throw error;
    }
  }

  /**
   * 设置配置数据到缓存
   */
  public async setConfigData(configType: string, data: CommonConfig[]): Promise<void> {
    const cache: CommonConfigCache = {
      data,
      timestamp: Date.now(),
      version: STORAGE_CONFIG.VERSION,
    };

    // 更新内存缓存
    this.memoryCache.set(configType, cache);

    // 更新storage缓存
    try {
      const cacheKey = this.getStorageKey(configType);
      localStorage.setItem(cacheKey, JSON.stringify(cache));
    } catch (error) {
    }
  }

  /**
   * 从storage获取缓存
   */
  private getFromStorage(configType: string): CommonConfigCache | null {
    try {
      const cacheKey = this.getStorageKey(configType);
      const cacheData = localStorage.getItem(cacheKey);
      if (cacheData) {
        return JSON.parse(cacheData);
      }
    } catch (error) {
    }
    return null;
  }

  /**
   * 获取storage键名
   */
  private getStorageKey(configType: string): string {
    return `${STORAGE_CONFIG.PREFIX}${configType}`;
  }

  /**
   * 检查缓存是否过期
   */
  private isExpired(timestamp: number): boolean {
    return Date.now() - timestamp > STORAGE_CONFIG.EXPIRY_TIME;
  }

  /**
   * 清除所有配置缓存
   */
  public clearAllCache(): void {
    // 清除内存缓存
    this.memoryCache.clear();

    // 清除storage缓存
    const configTypes = ['contact_info', 'website_info', 'record_info'];
    for (const configType of configTypes) {
      const cacheKey = this.getStorageKey(configType);
      localStorage.removeItem(cacheKey);
    }

  }

  /**
   * 清除指定配置类型的缓存
   */
  public clearConfigCache(configType: string): void {
    // 清除内存缓存
    this.memoryCache.delete(configType);

    // 清除storage缓存
    const cacheKey = this.getStorageKey(configType);
    localStorage.removeItem(cacheKey);

  }

  /**
   * 检查是否已初始化
   */
  public isReady(): boolean {
    return this.isInitialized;
  }
}

// 导出单例实例
export const commonConfigManager = CommonConfigManager.getInstance();
export default commonConfigManager;

